import { alpha, styled, keyframes } from '@mui/material/styles';
import {
  TextField,
  Button,
  Box,
  Modal,
  Typography,
  IconButton,
  FormControl,
  Select,
  Dialog,
  DialogTitle,
  Paper,
  ListItem,
  Divider,
  TableCell,
  tableCellClasses,
  TableRow,
  TableContainer,
} from '@mui/material';

export const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -60%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

// Modal Components
export const StyledModal = styled(Modal)({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
  },
});

export const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: '560px',
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows.heavy,
  overflow: 'hidden',
  animation: `${slideInFromTop} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
  maxHeight: '95vh',
  display: 'flex',
  flexDirection: 'column',
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  backgroundImage: theme.gradients.primary,
  backgroundSize: '200% auto',
  padding: theme.spacing(1.5, 2),
  color: theme.palette.common.white,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('xl')]: {
    padding: theme.spacing(3, 3.5),
  },
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const ModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflow: 'auto',
  flex: 1,
}));

// Form Components
export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.light,
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',

    '& fieldset': {
      borderColor: theme.palette.border.light,
      borderWidth: '1.5px',
    },

    '&:hover': {
      backgroundColor: theme.palette.background.lighter,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',

      '& fieldset': {
        borderColor: theme.palette.border.accent,
      },
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.common.white,
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0, 176, 155, 0.15)',

      '& fieldset': {
        borderColor: theme.palette.primary.rieker,
        borderWidth: '2px',
      },
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.background.disabled,

      '& fieldset': {
        borderColor: theme.palette.border.lighter,
      },
    },
  },

  '& .MuiInputLabel-root': {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,

    '&.Mui-focused': {
      color: theme.palette.primary.rieker,
    },
  },

  '& .MuiInputLabel-asterisk': {
    color: theme.palette.common.error,
  },

  '& .MuiFormHelperText-root': {
    fontSize: '0.8rem',
    marginLeft: theme.spacing(0.5),
  },
}));

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,

    '&.Mui-focused': {
      color: theme.palette.primary.rieker,
    },
  },

  '& .MuiInputLabel-asterisk': {
    color: theme.palette.common.error,
  },
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.light,
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.border.light,
    borderWidth: '1.5px',
  },

  '&:hover': {
    backgroundColor: theme.palette.background.lighter,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.accent,
    },
  },

  '&.Mui-focused': {
    backgroundColor: theme.palette.common.white,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows.focus,

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.rieker,
      borderWidth: '2px',
    },
  },
}));

// Button Components
export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'rotate(90deg) scale(1.1)',
  },
}));

export const StyledBackButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));
export const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundImage: theme.gradients.primary,
  backgroundSize: '200% auto',
  borderRadius: theme.spacing(3),
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: '0.95rem',
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  boxShadow: theme.shadows.light,
  transition: 'all 0.5s ease',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    transform: 'translateY(-2px)',
    backgroundPosition: 'right center',
  },

  '&:active': {
    transform: 'translateY(0)',
  },

  '&:disabled': {
    background: theme.shadows.disabled,
    color: theme.palette.text.helper,
    transform: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },

  '&:hover::before': {
    left: '100%',
  },
}));

export const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.error,
  backgroundColor: 'rgba(255, 68, 68, 0.1)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(255, 68, 68, 0.2)',
    transform: 'scale(1.1)',
  },
}));

export const AddUserButton = styled(Button)(({ theme }) => ({
  backgroundImage: theme.gradients.primary,
  backgroundSize: '200% auto',
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: '0.9rem',
  textTransform: 'none',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1, 3),
  boxShadow: theme.shadows.light,
  transition: 'all 0.3s ease',
  marginBottom: theme.spacing(2),

  '&:hover': {
    transform: 'translateY(-2px)',
    backgroundPosition: 'right center',
  },
}));

export const AddButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '10px',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  fontWeight: 600,
  color: theme.palette.text.white,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.6s ease, height 0.6s ease',
  },
  '&:hover::before': {
    width: '300px',
    height: '300px',
  },
  '&:hover': {
    boxShadow: '0 0 30px rgba(102, 126, 234, 0.12), 0 8px 24px rgba(102, 126, 234, 0.1)',
    transform: 'translateY(-4px) scale(1.01)',
  },
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '1rem',
  padding: theme.spacing(1, 2),
  textTransform: 'uppercase',
  border: `2px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
    transform: 'translateY(-1px)',
  },
}));

// Dialog Components
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows.heavy,
    overflow: 'hidden',
  },

  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
  },
}));

export const DialogHeaderStyled = styled(DialogTitle)(({ theme }) => ({
  backgroundImage: theme.gradients.primary,
  backgroundSize: '200% auto',
  color: theme.palette.common.white,
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2),

  '& .MuiTypography-root': {
    fontWeight: 700,
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

// List Components
export const UserListContainer = styled(Paper)(({ theme }) => ({
  maxHeight: '350px',
  overflowY: 'auto',
  overflowX: 'hidden',
  border: '1px solid rgba(0, 176, 155, 0.2)',
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.light,

  '&::-webkit-scrollbar': {
    width: 3,
    height: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 176, 155, 0.05)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 176, 155, 0.3)',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'rgba(0, 176, 155, 0.5)',
    },
  },
}));

export const UserListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.common.white,
  margin: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 176, 155, 0.1)',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: 'rgba(0, 176, 155, 0.05)',
    transform: 'translateX(4px)',
    borderColor: 'rgba(0, 176, 155, 0.3)',
  },
}));

// Utility Components
export const HelperText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.helper,
  fontSize: '0.8rem',
  fontStyle: 'italic',
  marginTop: theme.spacing(2),
  padding: theme.spacing(0, 1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

export const VerticalDivider = styled(Divider)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const StatusBox = styled(Box)(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 8px',
  borderRadius: '16px',
  backgroundColor: status === 'Active' ? '#e6f4ea' : '#fce8e8',
  '& .MuiTypography-root': {
    color: status === 'Active' ? '#1e8e3e' : '#d93025',
    fontSize: '14px',
    fontWeight: 550,
  },
}));

// Header Components
export const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2, 0, 2),
}));

export const HeaderSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
}));

export const SearchSection = styled(Box)({
  flex: 1,
  minWidth: 300,
});

export const StatsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(-1, -1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
}));

export const ButtonSection = styled(Box)({
  alignItems: 'center',
  justifyItems: 'flex-end',
});

export const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  border: '1px solid #667eea',
  borderRadius: '8px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
  },
}));

export const StatValue = styled(Typography)({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1976d2',
});

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  marginLeft: theme.spacing(0.5),
}));

// Table Components
export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.common.black,
    fontSize: '1rem',
    fontWeight: 550,
    padding: theme.spacing(1.5),
    '&:first-of-type': {
      paddingLeft: theme.spacing(3),
    },
    '&:last-child': {
      paddingRight: theme.spacing(3),
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '1rem',
    padding: theme.spacing(1),
    '&:first-of-type': {
      paddingLeft: theme.spacing(3),
    },
    '&:last-child': {
      paddingRight: theme.spacing(3),
    },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '100%',
  '&::-webkit-scrollbar': {
    width: 12,
    height: 12,
  },
  '&::-webkit-scrollbar-track': {
    background:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : alpha(theme.palette.common.black, 0.05),
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.2)
        : alpha(theme.palette.common.black, 0.2),
    borderRadius: 8,
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.3)
          : alpha(theme.palette.common.black, 0.3),
    },
  },
  '& .MuiTableCell-root.MuiTableCell-body:first-of-type': {
    paddingLeft: theme.spacing(2),
  },
  '& .MuiTableCell-root.MuiTableCell-head:first-of-type': {
    paddingLeft: theme.spacing(2),
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows.heavy,
  overflowY: 'auto',
  color: theme.palette.text.white,
}));

export const PageWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const TableWrapper = styled(Box)({
  width: '100%',
  margin: '0 auto',
  flex: 1,
  minHeight: 0,
});

// Auth styles
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUps = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;
const slideDowns = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

export const BoxContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(5),
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
  maxWidth: 480,
  margin: 'auto',
  position: 'relative',
  overflow: 'hidden',
  animation: `${slideUp} 0.6s ease-out`,
}));

export const AuthTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '1rem',
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    backgroundColor: '#fafafa',

    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light,
      },
    },

    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',

      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
    },
  },

  '& .MuiInputLabel-root': {
    fontWeight: 500,

    '&.Mui-focused': {
      color: theme.palette.secondary.main,
    },
  },

  '& .MuiFormHelperText-root': {
    marginLeft: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',

  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

export const StyledLogo = styled('img')({
  width: 'auto',
  height: '10vh',
  maxHeight: '80px',
  transition: 'transform 0.3s ease',
});

export const FormContainer = styled('form')(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const STATUS_STYLES = {
  PENDING: {
    bg: '#e8f0fe',
    color: '#1a73e8',
  },
  PROCESSING: {
    bg: '#fff4e5',
    color: '#f29900',
  },
  FINISHED: {
    bg: '#e6f4ea',
    color: '#1e8e3e',
  },
  FAILED: {
    bg: '#fce8e8',
    color: '#d93025',
  },
};

export const UploadStatusBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.PENDING;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '16px',
    backgroundColor: style.bg,

    '& .MuiTypography-root': {
      color: style.color,
      fontSize: '14px',
      fontWeight: 550,
    },
  };
});

export const FontName = styled(Box)(({ theme }) => ({
  fontSize: '1.1rem',
  background: theme.gradients.primary,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textDecoration: 'none',
  fontWeight: 700,
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0))',
  '&:hover': {
    textDecoration: 'none',
    filter: 'drop-shadow(0 4px 8px rgba(102, 126, 234, 0.25))',
    transform: 'scale(1.01)',
  },
}));

export const IconButtons = styled(IconButton)(({ theme }) => ({
  ml: 0.5,
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  color: '#667eea',
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0)',
  backgroundColor: 'rgba(102, 126, 234, 0.1)',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.2)',
    color: '#5a67d8',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
    transform: 'rotate(15deg) scale(1.01)',
  },
}));

export const flexAlignCenter = {
  display: 'flex',
  alignItems: 'center',
};

export const flexAlignCenterGap = (gap = 1, justifyContent = 'flex-start') => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent,
  gap,
});

export const flexColumnGap = (gap = 1) => ({
  display: 'flex',
  flexDirection: 'column',
  gap,
});

export const RightButton = styled(Button)(({ theme }) => ({
  flex: 1,
  textTransform: 'none',
  background: theme.gradients.primary,
  fontWeight: 600,
  fontSize: '0.9rem',
  borderRadius: '12px',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  color: 'white',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.6s ease, height 0.6s ease',
  },
  '&:hover::after': {
    width: '300px',
    height: '300px',
  },
  '&:hover': {
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-4px) scale(1.01)',
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
  },
}));

export const LeftButton = styled(Button)(({ theme }) => ({
  flex: 1,
  textTransform: 'none',
  borderColor: '#667eea',
  color: '#667eea',
  fontWeight: 600,
  fontSize: '0.9rem',
  borderRadius: '12px',
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent)',
    transition: 'left 0.5s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
  '&:hover': {
    borderColor: '#5a67d8',
    backgroundColor: 'rgba(102, 126, 234, 0.15)',
    color: '#5a67d8',
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.2)',
    transform: 'translateY(-3px)',
  },
}));

export const StyledSubmitBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showButton',
})(({ theme, showButton }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  left: 0,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  animation: showButton ? `${slideUps} 0.3s ease-out` : `${slideDowns} 0.3s ease-out`,
  opacity: showButton ? 1 : 0,
  pointerEvents: showButton ? 'auto' : 'none',
  transition: 'opacity 0.3s ease-out',
}));

export const Card = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.08)',
  padding: theme.spacing(2.5),
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 160,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102,126,234,0) 0%, rgba(102,126,234,0.05) 100%)',
    borderRadius: '12px',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    pointerEvents: 'none',
  },

  '&:hover::before': {
    opacity: 1,
  },

  '&:hover': {
    cursor: 'pointer',
    transform: 'translateY(-4px) scale(1.01)',
    boxShadow: '0 12px 24px rgba(102,126,234,0.1), 0 4px 12px rgba(0,0,0,0.08)',
    borderColor: 'rgba(102,126,234,0.3)',
  },

  boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: theme.palette.text.white,
  ':hover': {
    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
  },
}));

export const CenteredTypography = styled(Typography)(({ theme }) => ({
  backgroundClip: 'text',
  color: theme.palette.common.black,
  fontSize: '1.4rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));
